import type { IRewardsService } from '../../../services/rewards/IRewardsService';
import type { RewardsProfile } from '../../../services/rewards/types';
import { userProfileState } from '../stores/userProfileState';

export type ProfileState = {
  profile: RewardsProfile | null;
  loading: boolean;
  error: string | null;
};

export type ProfileListener = (state: ProfileState) => void;

export class ProfileManager {
  private state: ProfileState = { profile: null, loading: false, error: null };
  private listeners: ProfileListener[] = [];
  private realtimeListenerId: string | null = null;
  private destroyed = false;
  private fetchInFlight = false;
  private fetchQueued = false;
  /** Unread from getUnread that arrived before profile REST finished. */
  private pendingInboxUnreadCount: number | null = null;

  constructor(private readonly rewardsService: IRewardsService) {}

  init(): void {
    this.realtimeListenerId = userProfileState.addListener((realtimeState) => {
      if (this.destroyed || !realtimeState.authId) return;
      if (!this.state.profile) {
        this.fetchProfile();
        return;
      }
      this.updateState({
        profile: {
          ...this.state.profile,
          pointsBalance: realtimeState.pointsBalance,
          totalPointsEarned: realtimeState.totalPointsEarned,
          rank: realtimeState.rank,
          frozen: realtimeState.frozenAt != null,
        },
      });
    });
  }

  destroy(): void {
    this.destroyed = true;
    this.pendingInboxUnreadCount = null;
    if (this.realtimeListenerId) {
      userProfileState.removeListener(this.realtimeListenerId);
      this.realtimeListenerId = null;
    }
    this.listeners = [];
  }

  getState(): ProfileState {
    return this.state;
  }

  subscribe(listener: ProfileListener): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  async fetchProfile(): Promise<void> {
    if (this.fetchInFlight) {
      this.fetchQueued = true;
      return;
    }
    this.fetchInFlight = true;
    this.updateState({ loading: true });
    try {
      const data = await this.rewardsService.getProfile();
      if (this.destroyed) return;
      if (data && !('success' in data && !(data as any).success)) {
        // After REST completes, re-apply any fresher realtime state on top
        const rt = userProfileState.currentState;
        const restUnread =
          typeof data.inboxUnreadCount === 'number' ? data.inboxUnreadCount : 0;
        // Prefer the higher of REST vs a getUnread that won the startup race
        // (REST can briefly return 0 while the inbox active set is still settling).
        const pending = this.pendingInboxUnreadCount;
        this.pendingInboxUnreadCount = null;
        const inboxUnreadCount =
          pending !== null ? Math.max(restUnread, pending) : restUnread;
        const normalized: RewardsProfile = {
          ...data,
          inboxUnreadCount,
        };
        const profile = (rt.authId && rt.pointsBalance !== undefined)
          ? {
              ...normalized,
              pointsBalance: rt.pointsBalance,
              totalPointsEarned: rt.totalPointsEarned,
              rank: rt.rank,
              frozen: rt.frozenAt != null,
            }
          : normalized;
        this.updateState({ profile, error: null, loading: false });
      } else {
        this.updateState({ error: (data as any)?.error ?? 'Failed to load profile', loading: false });
      }
    } catch (err) {
      if (this.destroyed) return;
      this.updateState({
        error: err instanceof Error ? err.message : 'Failed to load profile',
        loading: false,
      });
    } finally {
      this.fetchInFlight = false;
      if (this.fetchQueued && !this.destroyed) {
        this.fetchQueued = false;
        void this.fetchProfile();
      }
    }
  }

  /** Optimistic badge update after a local mark-read; next fetchProfile corrects. */
  patchInboxUnreadCount(delta: number): void {
    if (!this.state.profile) return;
    const current = this.state.profile.inboxUnreadCount ?? 0;
    this.setInboxUnreadCount(current + delta);
  }

  /**
   * Absolute badge sync from inbox reads (hint / loadInbox / getUnread).
   * If profile REST has not landed yet, queue the count and merge on fetch.
   */
  setInboxUnreadCount(count: number): void {
    const next = Math.max(0, count);
    if (!this.state.profile) {
      this.pendingInboxUnreadCount = next;
      return;
    }
    this.pendingInboxUnreadCount = null;
    if ((this.state.profile.inboxUnreadCount ?? 0) === next) return;
    this.updateState({
      profile: {
        ...this.state.profile,
        inboxUnreadCount: next,
      },
    });
  }

  private updateState(partial: Partial<ProfileState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}
